import * as THREE from 'three';
import { Entity } from '../../entities';
import {
  createMatteMaterial,
  createHaloMaterial,
  createAtmosphereMaterial,
  createGlowMaterial
} from '../../visual';
import { createPlanetSurface, createCloudLayer } from '../../visual/textures';
import type { Landable, LandablePalette } from './Landable';

export interface PlanetOrbit {
  readonly center: THREE.Vector3;
  readonly radius: number;
  readonly speed: number;
  readonly initialAngle?: number;
  readonly y?: number;
}

export interface PlanetOptions {
  readonly radius: number;
  readonly base: number;
  readonly accent: number;
  readonly detail: number;
  readonly halo: number;
  readonly clouds: number;
  readonly position?: THREE.Vector3;
  readonly orbit?: PlanetOrbit;
  readonly spinSpeed?: number;
  readonly axialTilt?: number;
  readonly cloudDensity?: number;
  readonly displayName?: string;
  readonly ring?: {
    inner: number;
    outer: number;
    tilt?: number;
    opacity?: number;
  };
  /** World-space radius around the planet within which idle ships should be
   *  tugged along by the planet's orbital motion. Defaults to a multiple of
   *  the body radius; SectorBuilder bumps it to cover rings/belts. */
  readonly captureRadius?: number;
}

export class Planet extends Entity implements Landable {
  readonly radius: number;
  readonly displayName: string;
  readonly palette: LandablePalette;
  readonly captureRadius: number;

  private readonly bodyMesh: THREE.Mesh;
  private readonly cloudsMesh: THREE.Mesh | null;
  private readonly spinSpeed: number;
  private readonly cloudSpin: number;
  private readonly orbitCenter: THREE.Vector3 | null;
  private readonly orbitRadius: number;
  private readonly orbitSpeed: number;
  private readonly orbitY: number;
  private orbitAngle: number;

  constructor(private readonly options: PlanetOptions) {
    super();
    this.radius = options.radius;
    this.displayName = options.displayName ?? 'Planet';
    this.palette = {
      base: options.base,
      accent: options.accent,
      detail: options.detail,
      halo: options.halo,
      clouds: options.clouds
    };

    this.spinSpeed = options.spinSpeed ?? 0.045;
    this.cloudSpin = this.spinSpeed * 1.5;

    this.captureRadius =
      options.captureRadius ?? Math.max(options.radius * 2.5, 320);

    if (options.orbit) {
      this.orbitCenter = options.orbit.center.clone();
      this.orbitRadius = options.orbit.radius;
      this.orbitSpeed = options.orbit.speed;
      this.orbitY = options.orbit.y ?? options.orbit.center.y;
      this.orbitAngle = options.orbit.initialAngle ?? 0;
      this.applyOrbitPosition();
    } else {
      this.orbitCenter = null;
      this.orbitRadius = 0;
      this.orbitSpeed = 0;
      this.orbitY = 0;
      this.orbitAngle = 0;
      if (options.position) this.object3D.position.copy(options.position);
    }
    this.object3D.rotation.z = options.axialTilt ?? 0.25;

    this.bodyMesh = this.buildBody();
    this.cloudsMesh = this.buildClouds();
    this.buildInnerHalo();
    this.buildOuterHalo();
    if (options.ring) this.buildRing(options.ring);
  }

  getWorldPosition(out: THREE.Vector3): THREE.Vector3 {
    return out.copy(this.object3D.position);
  }

  /** Instantaneous orbital velocity (units/second) — the analytic derivative
   *  of the orbit parametrization. Returns the zero vector for fixed planets.
   *  Used by orbit-capture to translate idle ships along with the planet. */
  getOrbitalVelocity(out: THREE.Vector3): THREE.Vector3 {
    if (!this.orbitCenter) return out.set(0, 0, 0);
    const tangent = this.orbitRadius * this.orbitSpeed;
    return out.set(
      -Math.sin(this.orbitAngle) * tangent,
      0,
      Math.cos(this.orbitAngle) * tangent
    );
  }

  override update(dt: number): void {
    if (this.orbitCenter) {
      this.orbitAngle += this.orbitSpeed * dt;
      this.applyOrbitPosition();
    }
    this.bodyMesh.rotation.y += this.spinSpeed * dt;
    if (this.cloudsMesh) this.cloudsMesh.rotation.y += this.cloudSpin * dt;
  }

  private applyOrbitPosition(): void {
    if (!this.orbitCenter) return;
    this.object3D.position.set(
      this.orbitCenter.x + Math.cos(this.orbitAngle) * this.orbitRadius,
      this.orbitY,
      this.orbitCenter.z + Math.sin(this.orbitAngle) * this.orbitRadius
    );
  }

  private buildBody(): THREE.Mesh {
    const surface = createPlanetSurface({
      base: this.options.base,
      accent: this.options.accent,
      detail: this.options.detail
    });

    const material = createMatteMaterial({
      color: 0xffffff,
      roughness: 0.88,
      emissive: this.options.accent,
      emissiveIntensity: 0.04
    });
    material.map = surface;
    material.needsUpdate = true;

    const mesh = new THREE.Mesh(
      new THREE.SphereGeometry(this.options.radius, 64, 48),
      material
    );
    mesh.name = 'planet-body';
    this.object3D.add(mesh);
    return mesh;
  }

  private buildClouds(): THREE.Mesh | null {
    const density = this.options.cloudDensity ?? 0.85;
    if (density <= 0) return null;

    const alphaMap = createCloudLayer({
      density,
      tint: this.options.clouds
    });

    const material = new THREE.MeshStandardMaterial({
      color: this.options.clouds,
      roughness: 1,
      metalness: 0,
      transparent: true,
      opacity: 0.85,
      alphaMap,
      depthWrite: false
    });

    const mesh = new THREE.Mesh(
      new THREE.SphereGeometry(this.options.radius * 1.012, 64, 48),
      material
    );
    mesh.name = 'planet-clouds';
    this.object3D.add(mesh);
    return mesh;
  }

  private buildInnerHalo(): void {
    const halo = new THREE.Mesh(
      new THREE.SphereGeometry(this.options.radius * 1.045, 64, 48),
      createAtmosphereMaterial({
        color: this.options.halo,
        intensity: 1.6,
        power: 3.4,
        sunBoost: 0.55
      })
    );
    halo.name = 'planet-halo-inner';
    this.object3D.add(halo);
  }

  private buildOuterHalo(): void {
    const halo = new THREE.Mesh(
      new THREE.SphereGeometry(this.options.radius * 1.18, 56, 40),
      createAtmosphereMaterial({
        color: this.options.halo,
        intensity: 0.55,
        power: 2.0,
        sunBoost: 0.3
      })
    );
    halo.name = 'planet-halo-outer';
    this.object3D.add(halo);
  }

  private buildRing(ring: NonNullable<PlanetOptions['ring']>): void {
    const inner = ring.inner;
    const outer = ring.outer;
    const segments = 128;

    const geometry = new THREE.RingGeometry(inner, outer, segments, 6);
    const uv = geometry.attributes['uv'] as THREE.BufferAttribute;
    const position = geometry.attributes['position'] as THREE.BufferAttribute;
    for (let i = 0; i < position.count; i++) {
      const x = position.getX(i);
      const y = position.getY(i);
      const distance = Math.sqrt(x * x + y * y);
      const t = (distance - inner) / (outer - inner);
      uv.setXY(i, t, 1);
    }
    uv.needsUpdate = true;

    const gradientTexture = createRingGradient(this.options.accent);
    const material = createGlowMaterial(0xffffff, ring.opacity ?? 0.55);
    material.map = gradientTexture;
    material.side = THREE.DoubleSide;
    material.needsUpdate = true;

    const mesh = new THREE.Mesh(geometry, material);
    mesh.rotation.x = Math.PI / 2 - (ring.tilt ?? 0.3);
    mesh.name = 'planet-ring';
    this.object3D.add(mesh);
  }
}

function createRingGradient(accent: number): THREE.CanvasTexture {
  const width = 256;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = 1;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('ring gradient canvas unavailable');

  const r = (accent >> 16) & 0xff;
  const g = (accent >> 8) & 0xff;
  const b = accent & 0xff;
  const gradient = ctx.createLinearGradient(0, 0, width, 0);
  gradient.addColorStop(0.0, `rgba(${r},${g},${b},0)`);
  gradient.addColorStop(0.12, `rgba(${r},${g},${b},0.55)`);
  gradient.addColorStop(0.4, `rgba(${r},${g},${b},0.9)`);
  gradient.addColorStop(0.55, `rgba(${r},${g},${b},0.35)`);
  gradient.addColorStop(0.72, `rgba(${r},${g},${b},0.8)`);
  gradient.addColorStop(1.0, `rgba(${r},${g},${b},0)`);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, 1);

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  return tex;
}
